/**
 * opportunities/editOpportunity/editorContent.jsx - the editor's body: the
 * sticky hero header, the tab bodies and the footer, as one element the
 * pop-up and the page both place.
 * Moved verbatim out of EditOpportunityModal.jsx; the editor passes in the
 * values it reads.
 */
import CommentsPanel from '../../shared/CommentsPanel';
import AttachmentsPanel from '../../shared/AttachmentsPanel';
import TasksPanel from '../../tasks/TasksPanel';
import OpportunityActivityTab from '../OpportunityActivityTab';
import ProjectCostTabs, { COST_TABS } from '../../projectCosts/ProjectCostTabs';
import { STATUS_LIST } from '../editOpportunityConstants';
import { nextInvoiceNumber, splitInvoiceNumbers } from '../../../utils/docSuggest';
import { leftoverOf } from '../../../utils/projectMoney';
import { canSeeTab, canEdit, hasArea } from '../../../data/roles';
import { isProjectRow, PROJECT_STATUSES } from '../../../data/projectStatus';
import ProjectDailies from '../ProjectDailies';
import { OPP_FIELD_LABELS, oldQuoteListValues, mergeSavedFields } from '../editOpportunityForm';
import { LiveChangeBanner } from '../ConflictDialog';
import { renderHeroHeader } from '../EditOpportunityHeader';
import { renderSummaryTab, renderQuoteTab } from '../EditOpportunityTabs';
import { renderFooter } from '../EditOpportunityFooter';
import LinkedFiles from '../LinkedFiles';
import ChildrenSection from '../../projects/hierarchy/ChildrenSection';
import GroupSummary from '../../projects/hierarchy/GroupSummary';
import TrackingRollup from '../../projects/hierarchy/TrackingRollup';
import { Segmented } from '../../projectCosts/trackingUi';
import { FAMILY_BTN } from './editorBits';

export function makeContent({
  asPage,
  heroRef,
  heroTop,
  isCreate,
  opp,
  f,
  setF,
  shown,
  setTab,
  ownerName,
  grossMarginPct,
  marginColor,
  metaAccount,
  metaEstClose,
  metaEstRevenue,
  docHubCount,
  isProject,
  projectCards,
  pills,
  fromMatch,
  actuals,
  tabs,
  family,
  openFamily,
  scope,
  editable,
  setAddKind,
  liveNote,
  dismissLiveNote,
  bodyRef,
  lockFields,
  renderRow,
  siteLink,
  firstTaskProps,
  openedAsProject,
  allProjects,
  createdId,
  base,
  access,
  invoiceAdded,
  docs,
  noun,
  savedStatus,
  rebase,
  deletable,
  ksaCosts,
  trackRollup,
  setTrackRollup,
  openRecord,
  user,
  saveProject,
  setToast,
  totalCogs,
  quotedProfit,
  goPct,
  getPct,
  goGetPct,
  anticipatedValue,
  ksaCurrency,
  setCommentCount,
  stagedDocs,
  setStagedDocs,
  loadDocs,
  saveError,
  handleDelete,
  handleSave,
  leave,
  busy,
  backLabel,
  setShowComments,
  commentCount,
  canQuote,
  setQuoteOpen,
}) {
  const content = (
    <>
      {/* the pop-up's one scroll area: the sticky hero header, then the body */}
      <div style={asPage ? undefined : { flex: 1, minHeight: 0, overflowY: 'auto', background: '#FAFBF7' }}>
        {/* ── HERO HEADER (GAT-style) ── - in the pop-up it is the bar the window is
            dragged by (10/9/26, the Modal has no title of its own here) */}
        <div
          ref={heroRef}
          data-dialog-handle={asPage ? undefined : ''}
          style={asPage ? undefined : { position: 'sticky', top: heroTop, zIndex: 3 }}
        >
          {renderHeroHeader({
            isCreate,
            opp,
            f,
            setF,
            tab: shown,
            setTab,
            ownerName,
            grossMarginPct,
            marginColor,
            metaAccount,
            metaEstClose,
            metaEstRevenue,
            docHubCount,
            isProject,
            metaCards: isProject ? projectCards : null,
            pills,
            linkedFrom: fromMatch ? fromMatch[1] : null,
            actuals,
            tabs,
            family: family && { ...family, self: opp.id },
            onFamily: openFamily,
            familyActions:
              !scope && !isCreate && editable && isProjectRow(opp) ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setAddKind('line')}
                    style={FAMILY_BTN}
                    title="A new record for another line of this PO, under the same project number"
                  >
                    ＋ Add PO line
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddKind('sub')}
                    style={FAMILY_BTN}
                    title="A new sub-project (the next -0X0 number) under the same main project"
                  >
                    ＋ Add sub-project
                  </button>
                </div>
              ) : null,
          })}
        </div>

        {/* r50: someone else saved this record while it is open */}
        {liveNote && (
          <div style={{ padding: '8px 24px 0', background: '#FAFBF7' }}>
            <LiveChangeBanner note={liveNote} labels={OPP_FIELD_LABELS} onDismiss={dismissLiveNote} />
          </div>
        )}

        {/* ── BODY ── (full width; Doc Hub is its own tab) */}
        <div ref={bodyRef} style={{ padding: '16px 24px', background: '#FAFBF7' }}>
          {shown === 'summary' ? (
            lockFields(
              renderSummaryTab({
                renderRow,
                siteLink,
                firstTask: firstTaskProps,
                f,
                isCreate,
                statuses: isCreate && openedAsProject ? PROJECT_STATUSES : STATUS_LIST,
                // the KSA tab types its invoicing; the main tab's is the invoice ledger
                invoice: isProject && scope ? { f, setF, nextNumber: () => nextInvoiceNumber(allProjects) } : null,
                ledger:
                  isProject && !scope
                    ? {
                        projectId: isCreate ? createdId : opp.id,
                        base,
                        editable,
                        nextNumber: () => nextInvoiceNumber(allProjects),
                        tabLink: canSeeTab(access, 'finance-invoices'),
                        ledgerEdit: canEdit(access, 'finance-invoices'),
                        onAdded: invoiceAdded,
                      }
                    : null,
                leftover: (() => {
                  const l = leftoverOf({ poValue: f.poValue, invoicedAlready: f.invoicedAlready });
                  return l == null ? '' : String(l);
                })(),
                oldList: isCreate ? [] : oldQuoteListValues(opp),
                // the PO and invoices are a project's (their fields are hidden on an opportunity)
                files: isCreate ? null : (
                  <LinkedFiles
                    docs={docs}
                    quote={f.quoteLetter}
                    po={isProject ? f.poNumber : ''}
                    invoices={isProject ? f.invoiceNumbers : ''}
                    base={base}
                  />
                ),
                mainProject: scope
                  ? null
                  : {
                      records: allProjects || [],
                      selfId: isCreate ? null : opp.id,
                      customer: f.customer,
                      value: f.parentId,
                      onChange: (v) => setF((p) => ({ ...p, parentId: v })),
                    },
              })
            )
          ) : shown === 'tasks' ? (
            <TasksPanel
              projectId={opp.id}
              entityLabel={`${f.quoteLetter ? `Q${f.quoteLetter} · ` : ''}${f.name || opp.name || opp.description || noun.toLowerCase()}`}
              editable={editable}
              // the next-step prompt can close this opportunity (T-04);
              // keep the form in step so a later Save does not reopen it.
              // Closing as Won asks for the PO itself, and hands back what
              // that form saved (`patch`): merged here, or a later Save
              // would send the blank PO fields this form opened with.
              onHomeUpdated={(h) => {
                if (!h || h.kind !== 'opportunity') return;
                if (h.id != null && String(h.id) !== String(opp.id)) return;
                if (h.status) savedStatus.current = h.status;
                const saved = { ...(h.patch || {}), ...(h.status ? { status: h.status } : {}) };
                setF((p) => mergeSavedFields(p, saved));
                // already on the server: the next Save neither resends nor expects the old values
                const kept = Object.fromEntries(Object.entries(saved).filter(([, v]) => v != null));
                rebase(mergeSavedFields(f, saved), kept);
              }}
            />
          ) : shown === 'dailies' ? (
            <ProjectDailies projectId={opp.id} />
          ) : shown === 'timeline' ? (
            // AL-01 / AL-09: entries tagged to this opportunity + its own events.
            // Its own component so the modal reads no new useApp() keys (KSA).
            <OpportunityActivityTab opp={opp} editable={editable} deletable={deletable} />
          ) : shown === 'costs' ? (
            // r44: purchases in any currency and the profit in the record's own
            // (SAR), for quoting with Planned lines. Reads no useApp() keys.
            ksaCosts
          ) : COST_TABS.includes(shown) ? (
            // Tracking: Summary / Log time & expenses / Materials / Setup over one load (ProjectCostTabs);
            // a main record can show every record under it added up instead (TrackingRollup)
            <>
              {family && family.isMainRecord && (
                <div style={{ marginBottom: 14 }}>
                  <Segmented
                    label="Tracking of"
                    value={trackRollup ? 'all' : 'this'}
                    onChange={(v) => setTrackRollup(v === 'all')}
                    options={[
                      { value: 'this', label: 'This record' },
                      { value: 'all', label: `Main project roll-up (${family.main.members.filter(isProjectRow).length})` },
                    ]}
                  />
                </div>
              )}
              {family && family.isMainRecord && trackRollup ? (
                <TrackingRollup records={family.main.members.filter(isProjectRow)} onOpen={(id) => openRecord(id, 'tracking')} />
              ) : (
                <ProjectCostTabs
                  tab={shown}
                  project={opp}
                  editable={canEdit(access, 'projects')}
                  seesCosts={hasArea(access, 'cost-rates', 'view')}
                  canSeeInvoices={canSeeTab(access, 'finance-invoices')}
                  userKey={user && user.email}
                />
              )}
            </>
          ) : shown === 'family' ? (
            // its Children (linked by hand), then the whole family, its figures and the tracking roll-up
            <>
              <ChildrenSection
                record={opp}
                all={allProjects || []}
                editable={editable}
                saveProject={saveProject}
                onOpen={(id) => openRecord(id, 'family')}
                onDone={setToast}
              />
              {family && (
                <GroupSummary
                  main={family.main}
                  currentId={opp.id}
                  onOpen={(id) => openRecord(id)}
                  tracking={canSeeTab(access, 'projects')}
                />
              )}
            </>
          ) : shown === 'quote' ? (
            lockFields(
              renderQuoteTab({
                renderRow,
                totalCogs,
                grossMarginPct,
                quotedProfit,
                marginColor,
                goPct,
                getPct,
                goGetPct,
                anticipatedValue,
                ksaCurrency,
                isProject,
              })
            )
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isCreate ? '1fr' : '1fr 1fr', gap: 24, alignItems: 'start' }}>
              {/* Comments need a record to hang on; files do not, because
                      they are staged until the save. */}
              {!isCreate && (
                <div style={{ background: '#fff', border: '1px solid #E5E9DD', borderRadius: 10, padding: '16px 18px' }}>
                  <CommentsPanel projectId={opp.id} onCount={setCommentCount} />
                </div>
              )}
              <div style={{ background: '#fff', border: '1px solid #E5E9DD', borderRadius: 10, padding: '16px 18px' }}>
                {isCreate && (
                  <div style={{ fontSize: 10.5, color: '#B07503', fontWeight: 700, marginBottom: 10 }}>
                    {stagedDocs.length
                      ? `${stagedDocs.length} file${stagedDocs.length === 1 ? '' : 's'} ready. They upload when you save this ${noun.toLowerCase()}.`
                      : `Add files now; they upload when you save this ${noun.toLowerCase()}.`}
                  </div>
                )}
                <AttachmentsPanel
                  projectId={isCreate ? '' : opp.id}
                  staged={stagedDocs}
                  onStage={setStagedDocs}
                  canDelete
                  onChanged={loadDocs}
                  suggest={{
                    quote: f.quoteLetter || '',
                    po: f.poNumber || opp.poNumber || opp.po_number || '',
                    invoices: splitInvoiceNumbers(f.invoiceNumbers || opp.invoiceNumbers || opp.invoice_numbers),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      {saveError && (
        <div
          role="alert"
          style={{
            padding: '8px 24px',
            flexShrink: 0,
            background: '#FFEBEE',
            borderTop: '1px solid #FFCDD2',
            color: '#B71C1C',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {saveError}
        </div>
      )}
      {renderFooter({
        canDelete: !isCreate && deletable,
        editable,
        handleDelete,
        handleSave,
        onClose: leave,
        isCreate,
        noun,
        saving: busy,
        closeLabel: asPage ? backLabel : 'Cancel',
        onComment: () => setShowComments((s) => !s),
        commentCount,
        // r53: the quote builder for this record (components/quotes). It
        // downloads the document; it saves nothing on the record.
        extra: canQuote ? (
          <>
            <button
              type="button"
              onClick={() => setQuoteOpen(true)}
              style={{
                background: '#fff',
                color: '#1B5E20',
                border: '1px solid #1B5E20',
                padding: '9px 16px',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'Montserrat',
                flexShrink: 0,
              }}
            >
              Build quote
            </button>
            <span style={{ fontSize: 11, color: '#666', lineHeight: 1.35 }}>
              {scope
                ? 'Make the Service or Parts quote document from this KSA record, priced on the KSA quote rate card.'
                : `Make the Service or Parts quote document from this ${noun.toLowerCase()}.`}
            </span>
          </>
        ) : null,
      })}
    </>
  );
  return content;
}
